import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { GAINS_VERSION } from 'app/shared/constants/common-items';
import { GAINS_SIDENAV_BOTTOMGROUP_ITEMS, GAINS_SIDENAV_ITEMS } from 'app/shared/constants/gains-sidenav-items';
import { EllipsisMenuItem } from '@gainsystems/gains-ui-commons';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AuthService } from 'app/services/auth/auth.service';
import { FeatureFlagsService } from '../../services/feature-flags/feature-flags.service';
import { FEATURE_FLAG_PERSONAL_SETTINGS } from '../../services/feature-flags/feature-flags.constants';
import { Subject, combineLatest, takeUntil } from 'rxjs';
import { RailItemConfig } from 'app/shared/models/rail-item.model';
import { NotificationsService } from '../../services/notifications/notifications.service';
import { PermissionsService } from '../../services/permissions/permissions.service';

@Component({
  selector: 'gains-sidenav',
  standalone: false,
  templateUrl: './gains-sidenav.component.html',
  styleUrl: './gains-sidenav.component.scss'
})
export class GainsSidenavComponent implements OnInit, OnDestroy {
  @Input() sidenavCollapsed: any;
  @Output() sidenavCollapsedChange = new EventEmitter<void>();
  @Input() profileDropDownClass?: string;
  sidenavMenu = GAINS_SIDENAV_ITEMS;
  bottomGroupMenu: RailItemConfig[] = GAINS_SIDENAV_BOTTOMGROUP_ITEMS;
  notificationUnreadCount = 0;
  gainsVersion = GAINS_VERSION;
  loggedInUser = 'Bob Smith';
  private readonly destroy$ = new Subject<void>();
  ellipsisDataItems: EllipsisMenuItem[] = [];
  modalRef: any;
  @ViewChild('logoutModalFooter', { static: true }) logoutModalFooter!: TemplateRef<any>;

  constructor(
    private readonly _router: Router,
    private readonly _modalService: NzModalService,
    private readonly _authService: AuthService,
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _notificationsService: NotificationsService,
    private readonly _permissionsService: PermissionsService
  ) {}

  ngOnInit(): void {
    // Ensure flags are fetched even if no feature-guarded route triggered the load.
    void this._featureFlagsService.ensureFeatureFlagsLoaded();
    void this._permissionsService.ensurePermissionsLoaded();

    combineLatest([this._featureFlagsService.featureFlags$, this._permissionsService.permissions$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.sidenavMenu = this.filterMenu(GAINS_SIDENAV_ITEMS);
        this.bottomGroupMenu = this.filterMenu(GAINS_SIDENAV_BOTTOMGROUP_ITEMS);
        this.ellipsisDataItems = this.buildEllipsisItems();
      });

    // Keeps the Notification badge live as alerts are raised and reviewed.
    this._notificationsService.unreadCount$.pipe(takeUntil(this.destroy$)).subscribe(count => {
      this.notificationUnreadCount = count;
    });
  }

  /** Unread count feeding a row's badge, or zero for the rows that have none. */
  getBadgeCount(item: RailItemConfig): number {
    return item?.badgeKey === 'notifications' ? this.notificationUnreadCount : 0;
  }

  /** Handles a rail row that raises an action instead of navigating. */
  onRailItemActivated(item: RailItemConfig): void {
    if (item?.action === 'open-notifications') {
      this._notificationsService.requestOpenTray();
    }
  }

  trackByMenu(index: number, item: RailItemConfig): string {
    return item.menu;
  }

  private buildEllipsisItems(): EllipsisMenuItem[] {
    const items: EllipsisMenuItem[] = [];

    if (this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_PERSONAL_SETTINGS)) {
      items.push({
        title: 'Personal Settings',
        icon: 'user-star.svg',
        disabled: false
      });
    }

    items.push({
      title: 'Log Out',
      icon: 'logout.svg',
      disabled: false,
      divider: items.length > 0,
      warning: true
    });

    return items;
  }

  getFullName() {
    return this._authService.getFullName();
  }

  getActiveRoute(route: string): boolean {
    return this._router.url === route;
  }

  isAnySubmenuItemActive(): boolean {
    return this.sidenavMenu.some(item => this.isSubmenuItemActive(item));
  }

  isSingleMenuActive(): boolean {
    return this.sidenavMenu.some(item => !item.subMenu?.length && this.isMenuItemActive(item));
  }

  isSubmenuItemActive(item: any): boolean {
    // Prevent activating submenu if current route is '/home'
    if (this._router.url === '/home' && item.menu !== 'Home') {
      return false;
    }
    // Activate submenu only if current route matches one of its subMenu routes
    return item?.subMenu?.some((sub: { routerLink: string }) => this._router.url.startsWith(sub.routerLink));
  }

  isMenuItemActive(item: any): boolean {
    if (this._router.url === '/home' && item.menu !== 'Home') {
      return false;
    }
    return item?.routerLink && this.isOnRouteOrChild(item.routerLink);
  }

  isTopGroupItemActive(): boolean {
    return this.sidenavMenu.some(item => this.isMenuItemActive(item) || this.isSubmenuItemActive(item));
  }

  isBottomMenuItemActive(item: any): boolean {
    if (this._router.url === '/home' && item.menu !== 'Home') {
      return false;
    }
    return item?.routerLink && this.currentPath() === item.routerLink;
  }

  isBottomGroupItemActive(): boolean {
    return this.bottomGroupMenu.some(item => this.isBottomMenuItemActive(item));
  }

  onEllipsisAction(action: string) {
    if (action === 'Personal Settings') {
      this._router.navigate(['/settings/personal-settings']);
      return;
    }

    if (action === 'Log Out') {
      // Collapse sidenav immediately when logout is clicked
      this.sidenavCollapsedChange.emit();

      this.modalRef = this._modalService.create({
        nzContent: 'Are you sure you want to log out? You willl need to sign in again to continue using the system.',
        nzWidth: '512px',
        nzClassName: 'change-model-modal',
        nzCentered: true,
        nzTitle: 'Log Out',
        nzFooter: this.logoutModalFooter,
        nzNoAnimation: true,
        nzOnCancel: () => this.modalRef.close()
      });
    }
  }
  onLogout() {
    this._authService.logout();
    this.modalRef.close();
  }
  private filterMenu(menuItems: any[]) {
    return menuItems
      .map(item => {
        // Check if parent menu item is enabled by feature flag
        if (!this.isFeatureEnabledByFlag(item) || !this.hasRequiredPermission(item)) {
          return null;
        }

        if (item.subMenu?.length) {
          const subMenu = item.subMenu.filter((sub: any) => this.isFeatureEnabledByFlag(sub));
          return subMenu.length ? { ...item, subMenu } : null;
        }
        return item;
      })
      .filter(Boolean);
  }

  private isFeatureEnabledByFlag(item: any): boolean {
    // If item has a featureFlag property, check it with the service
    if (item.featureFlag) {
      return this._featureFlagsService.isFeatureEnabled(item.featureFlag);
    }
    // If no feature flag is specified, default to enabled
    return true;
  }

  private hasRequiredPermission(item: RailItemConfig): boolean {
    return !item.permission || this._permissionsService.hasPermission(item.permission);
  }

  // Access Control keeps its tab in the query string, which must not unlight its row.
  private currentPath(): string {
    return this._router.url.split('?')[0];
  }

  private isOnRouteOrChild(routerLink: string): boolean {
    const path = this.currentPath();
    return path === routerLink || path.startsWith(`${routerLink}/`);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
